import { createTheme, lighten } from '@mui/material/styles'

type Gradient = {
  sideNav: {
    start: string
    mid: string
    stop: string
  }

  button: {
    start: string
    stop: string
  }
}

type Border = {
  outline: string
  separator: string
}

type FontSize = {
  xs: string
  sm: string
  base: string
  lg: string
  xl: string
  xxl: string
  xxxl: string
}

type Boldness = {
  base: number
  bold: number
  bolder: number
}

declare module '@mui/material/styles' {
  // Add extra properties to theme
  interface Theme {
    gradient: Gradient
    border: Border
    size: FontSize
    bold: Boldness
  }

  // Allow configuration of extra properties using `createTheme`
  interface ThemeOptions {
    gradient: Partial<Gradient>
    border: Border
    size: FontSize
    bold: Boldness
  }

  // Add extra properties to palette
  interface Palette {
    altButtonText: {
      main: string
    }

    button: {
      text: string
      main: string
    }

    textfield: {
      border: string
      empty: string
      focused: string
      focusGlow: string
    }

    select: {
      arrow: string
    }

    loginError: {
      text: string
      bg: string
    }

    indicator: {
      red: string
      orange: string
      green: string
    }

    banner: {
      alert: string
      offline: string
    }

    notification: {
      peer: string
      group: string
      conversation: string
      notification: string
      dot: string
      row: string
    }

    variant: {
      success: string
      error: string
    }

    switch: {
      thumb: string
      track: string
      thumbChecked: string
      trackChecked: string
      thumbDisabled: string
      trackDisabled: string
      off: string
    }

    textAlt: string
    textLight: string
    textLightFaded: string

    border: string
    accent: string
    secondaryAccent: string
    buttonBG: string
    buttonGradient: string
    avatarBackground: string
    online: string
    away: string
    mobileIcon: string
    altColor: string
  }

  // Allow configuration of palette using `createTheme`
  interface PaletteOptions {
    altButtonText: {
      main: string
    }

    button: {
      text: string
      main?: string
    }

    textfield: {
      border: string
      empty: string
      focused: string
      focusGlow: string
    }

    select: {
      arrow: string
    }

    loginError: {
      text: string
      bg: string
    }

    indicator: {
      red: string
      orange: string
      green: string
    }

    banner: {
      alert: string
      offline: string
    }

    notification: {
      peer: string
      group: string
      conversation: string
      notification: string
      dot: string
      row: string
    }

    variant: {
      success: string
      error: string
    }

    switch: {
      thumb: string
      track: string
      thumbChecked: string
      trackChecked: string
      thumbDisabled: string
      trackDisabled: string
      off: string
    }

    textAlt: string
    textLight: string
    textLightFaded: string

    border: string
    accent: string
    secondaryAccent: string
    buttonBG: string
    buttonGradient: string
    avatarBackground: string
    online: string
    away: string
    mobileIcon: string
    altColor: string
  }
}

let theme = createTheme({
  palette: {
    mode: 'light',
    tonalOffset: 0.65,
    divider: '#F1F1F1',

    primary: {
      main: '#B64FCD'
    },

    secondary: {
      main: '#F8D8FF'
    },

    // Extra palette variables

    altButtonText: {
      main: '#E8E8E8'
    },

    button: {
      text: '#B64FCD'
    },

    textfield: {
      border: '#EFEFEF',
      empty: '#F6F8F9',
      focused: '#25AAF0',
      focusGlow: 'rgba(0, 123, 255, 0.25)'
    },

    select: {
      arrow: '#B1B7BA'
    },

    loginError: {
      text: 'white',
      bg: '#E64040'
    },

    indicator: {
      red: '#E64040',
      orange: '#F48E16',
      green: '#60D66A'
    },

    banner: {
      alert: '#FAF7D8',
      offline: '#EEEEEE'
    },

    notification: {
      peer: '#CB2AE1',
      group: '#26C0AF',
      conversation: '#B64FCD',
      notification: '#B7B7B7',
      dot: '#D724E7',
      row: '#BAE7F291'
    },

    variant: {
      success: '#06C270',
      error: '#FF3B3B'
    },

    switch: {
      thumb: '#fcfcfc',
      track: '#e7ebed',
      thumbChecked: '#fbfbfb',
      trackChecked: '#0ea0db',
      thumbDisabled: '#fdfdfd',
      trackDisabled: '#7fd8fb',
      off: '#ced1d2'
    },

    textAlt: '#202122',
    textLight: '#4A4E51',
    textLightFaded: 'rgba(127,132,135, 0.7)',

    border: '#D3D3D3',
    accent: '#25AAF0',
    secondaryAccent: '#657786',
    buttonBG: '#EEE',
    buttonGradient: 'linear-gradient(276deg, #B64FCD 0%, #F8D8FF 100%)',
    avatarBackground: '#E4E8EA',
    online: 'rgba(0, 230, 64, 1)',
    away: '#E8B425',
    mobileIcon: '#65BF95',
    altColor: lighten('#185c7d', 0.3)
  },

  typography: {
    fontFamily: ['Inter', 'sans-serif'].join(','),
    fontSize: 16
  },

  // Extra properties

  gradient: {
    sideNav: {
      start: '#55F4CC',
      mid: '#5F618F',
      stop: '#263D76'
    }
  },

  border: {
    outline: 'rgba(173, 194, 204, 0.9)',
    separator: 'rgba(230, 234, 236, 0.9)'
  },

  size: {
    xs: '0.825rem',
    sm: '0.95rem',
    base: '1.15rem',
    lg: '1.35rem',
    xl: '1.55rem',
    xxl: '1.8rem',
    xxxl: '2.8rem'
  },

  bold: {
    base: 400,
    bold: 700,
    bolder: 900
  },
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          '&&:hover': {
            backgroundColor: lighten('#B64FCD', 0.2)
          }
        }
      }
    }
  }
})

// Augment values that depend on theme values
theme = createTheme(theme, {
  palette: {
    button: {
      main: lighten(theme.palette.primary.main, 0.35)
    }
  },

  gradient: {
    button: {
      start: theme.palette.primary.main,
      stop: lighten(theme.palette.primary.main, 0.35)
    }
  }
})

export default theme
